import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const wsUrl = () => {
  const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProto}//${window.location.host}/api/ws/activity`;
};

const iconForType = (t) => {
  if (!t) return '•';
  if (t.includes('booking')) return '🎟️';
  if (t.includes('luggage')) return '🧳';
  if (t.includes('passport')) return '🛂';
  if (t.includes('whatsapp')) return '💬';
  if (t.includes('feature_flag')) return '⚙️';
  if (t.includes('fleet') || t.includes('trip')) return '🚌';
  return '•';
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleString();
};

const ActivityFeed = ({ limit = 15 }) => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  // Initial load
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/enterprise/realtime/activity?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setEvents(r.data?.events || []))
      .catch(() => setEvents([]));
  }, [token, limit]);

  // WebSocket
  useEffect(() => {
    if (!token) return;
    let alive = true;
    let retries = 0;
    let pingTimer = null;

    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(wsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          retries = 0;
          // Keep-alive ping
          pingTimer = setInterval(() => {
            try { ws.send('ping'); } catch (_) { /* ignore */ }
          }, 25000);
        };
        ws.onmessage = (msg) => {
          try {
            const ev = JSON.parse(msg.data);
            setEvents(prev => [ev, ...prev].slice(0, limit));
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => {
          setConnected(false);
          if (pingTimer) clearInterval(pingTimer);
          if (alive) {
            retries++;
            const delay = Math.min(15000, 1000 * Math.pow(2, retries));
            setTimeout(connect, delay);
          }
        };
        ws.onerror = () => { /* onclose will handle */ };
      } catch (_) {
        setConnected(false);
      }
    };

    connect();
    return () => {
      alive = false;
      if (pingTimer) clearInterval(pingTimer);
      try { wsRef.current?.close(); } catch (_) { /* ignore */ }
    };
  }, [token, limit]);

  return (
    <Card className="card-panashe" data-testid="activity-feed">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Activity className="h-5 w-5 text-indigo-600" />
          Live Activity
          {connected ? (
            <Badge variant="outline" className="text-emerald-600 border-emerald-200 ml-auto">
              <Wifi className="h-3 w-3 mr-1" /> Live
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-400 ml-auto">
              <WifiOff className="h-3 w-3 mr-1" /> Reconnecting
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No recent activity. Events will appear here in real time.</p>
        ) : (
          <ul className="space-y-2 max-h-[320px] overflow-y-auto">
            {events.map((e, i) => (
              <li
                key={e.id || i}
                className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded-md text-sm"
                data-testid={`activity-event-${i}`}
              >
                <span className="text-base leading-none mt-0.5">{iconForType(e.type)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-800 truncate">{e.summary || e.type}</p>
                  <p className="text-xs text-slate-400">{timeAgo(e.ts)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
